/**
 * Board Governance — Art. 20 NIS2 · Aprovações + Formações + Board Pack PDF.
 * Route: /board
 */
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Briefcase, Users, GraduationCap, FileText, Download, ShieldCheck, Loader2, Plus, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AnnualComplianceDashboard } from '../components/AnnualComplianceDashboard';
import { UpcomingReviewsWidget } from '../components/UpcomingReviewsWidget';
import { ReviewCompletionStatsCard } from '../components/ReviewCompletionStatsCard';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BoardGovernance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isBoard = user?.role === 'board_member';
  const canManage = isAdmin || isBoard;

  const [stats, setStats] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [showApproval, setShowApproval] = useState(false);
  const [showTraining, setShowTraining] = useState(false);
  const [savingA, setSavingA] = useState(false);
  const [savingT, setSavingT] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const currentYear = new Date().getFullYear();

  const [approvalForm, setApprovalForm] = useState({
    year: currentYear,
    scope_summary: '',
    approver_names: '',
    approved_documents: '',
    notes: '',
  });
  const [trainingForm, setTrainingForm] = useState({
    member_email: '',
    member_name: '',
    course_title: '',
    provider: '',
    hours: 2,
    completed_at: new Date().toISOString().slice(0, 10),
    topics: '',
    certificate_ref: '',
  });

  useEffect(() => { document.title = 'Órgão de Gestão · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const [s, a, t, m] = await Promise.all([
          api.get('/board/stats'),
          api.get('/board/approvals'),
          api.get('/board/trainings'),
          api.get('/board/members'),
        ]);
        if (ignore) return;
        setStats(s.data);
        setApprovals(a.data.items || []);
        setTrainings(t.data.items || []);
        setMembers(m.data.members || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar Board Governance.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [refresh]);

  const submitApproval = async () => {
    if (!approvalForm.scope_summary || approvalForm.scope_summary.length < 20) {
      toast.error('Resumo do âmbito deve ter pelo menos 20 caracteres.');
      return;
    }
    setSavingA(true);
    try {
      await api.post('/board/approvals', {
        year: Number(approvalForm.year),
        scope_summary: approvalForm.scope_summary,
        approver_names: approvalForm.approver_names.split(',').map(s => s.trim()).filter(Boolean),
        approved_documents: approvalForm.approved_documents.split(',').map(s => s.trim()).filter(Boolean),
        notes: approvalForm.notes || null,
      });
      toast.success('Aprovação registada com assinatura digital.');
      setShowApproval(false);
      setApprovalForm({ ...approvalForm, scope_summary: '', approver_names: '', approved_documents: '', notes: '' });
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao registar.');
    } finally {
      setSavingA(false);
    }
  };

  const submitTraining = async () => {
    if (!trainingForm.member_email || !trainingForm.member_name || !trainingForm.course_title) {
      toast.error('Email, nome e curso são obrigatórios.');
      return;
    }
    setSavingT(true);
    try {
      await api.post('/board/trainings', {
        ...trainingForm,
        hours: Number(trainingForm.hours),
        topics: trainingForm.topics.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Formação registada.');
      setShowTraining(false);
      setTrainingForm({ ...trainingForm, member_email: '', member_name: '', course_title: '', provider: '', topics: '', certificate_ref: '' });
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao registar.');
    } finally {
      setSavingT(false);
    }
  };

  const downloadPack = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/board/pack.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const hash = res.headers.get('X-Content-Hash-SHA256');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `board_pack_${currentYear}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Board Pack exportado · SHA-256: ${hash?.slice(0, 10)}…`);
    } catch (err) {
      toast.error('Falha ao exportar Board Pack.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout title="Órgão de Gestão">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="board-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-indigo-600" /> Órgão de Gestão
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Aprovação anual das medidas de gestão de risco (<strong>Art. 20(1) NIS2</strong>) e registo de formação obrigatória dos membros do CdA (<strong>Art. 20(2)</strong>).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadPack} disabled={downloading} data-testid="board-pack-btn">
              {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
              Board Pack PDF
            </Button>
            {canManage && (
              <>
                <Button variant="outline" onClick={() => setShowTraining(true)} data-testid="board-add-training-btn">
                  <GraduationCap className="h-3.5 w-3.5 mr-1.5" /> Registar formação
                </Button>
                <Button onClick={() => setShowApproval(true)} data-testid="board-add-approval-btn">
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Nova aprovação
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Alert if current year approval is missing */}
        {!loading && stats && !stats.approval_current_year && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="board-alert-missing-approval">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">Aprovação anual em falta para {stats.current_year}</p>
              <p className="text-xs text-red-700 mt-1">
                O Art. 20(1) NIS2 exige aprovação formal anual pelas administrações executivas.
                Sem este registo, a organização fica exposta em auditoria CNCS.
              </p>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="board-kpi-approval">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Aprovação {stats?.current_year}</span>
                {stats?.approval_current_year ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-red-600" />}
              </div>
              <div className={`text-lg font-bold tabular-nums ${stats?.approval_current_year ? 'text-emerald-700' : 'text-red-700'}`}>
                {stats?.approval_current_year ? 'Registada' : 'Em falta'}
              </div>
              {stats?.last_approval && (
                <div className="text-xs text-slate-500 mt-1">Última: {stats.last_approval.year}</div>
              )}
            </CardContent>
          </Card>
          <Card data-testid="board-kpi-members">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Membros ativos</span>
                <Users className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900 tabular-nums">{stats?.total_members ?? 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="board-kpi-coverage">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Cobertura formação YTD</span>
                <GraduationCap className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900 tabular-nums">{stats?.training_coverage_pct ?? 0}%</div>
              <div className="text-xs text-slate-500 mt-1">{stats?.trained_members_ytd ?? 0} de {stats?.total_members ?? 0} membros</div>
            </CardContent>
          </Card>
          <Card data-testid="board-kpi-trainings">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Formações registadas</span>
                <FileText className="h-4 w-4 text-sky-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900 tabular-nums">{stats?.total_trainings ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">{stats?.trainings_ytd ?? 0} no ano corrente</div>
            </CardContent>
          </Card>
        </div>

        {/* Painel de Cumprimento Anual (compacto) — cross-framework overview for the Board */}
        <AnnualComplianceDashboard
          compact
          title="Cumprimento Anual — Vista para o Órgão de Gestão"
          onOpenFull={() => navigate('/compliance/calendar')}
        />

        {/* Revisões próximas — Políticas, Fornecedores, DPIAs, Auditorias */}
        <div className="grid gap-6 lg:grid-cols-2">
          <UpcomingReviewsWidget days={180} limit={8} />
          <ReviewCompletionStatsCard />
        </div>

        <Tabs defaultValue="approvals">
          <TabsList>
            <TabsTrigger value="approvals" data-testid="tab-approvals">Aprovações</TabsTrigger>
            <TabsTrigger value="trainings" data-testid="tab-trainings">Formações</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Membros CdA</TabsTrigger>
          </TabsList>

          <TabsContent value="approvals">
            <Card>
              <CardContent className="p-0">
                {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
                  approvals.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem aprovações registadas ainda.</div> :
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">Ano</th>
                        <th className="text-left px-4 py-3">Âmbito</th>
                        <th className="text-left px-4 py-3">Aprovadores</th>
                        <th className="text-left px-4 py-3">Assinado por</th>
                        <th className="text-left px-4 py-3">Hash</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {approvals.map(a => (
                        <tr key={a.approval_id} className="hover:bg-slate-50" data-testid={`approval-${a.approval_id}`}>
                          <td className="px-4 py-3 font-bold tabular-nums">{a.year}</td>
                          <td className="px-4 py-3 max-w-md truncate" title={a.scope_summary}>{a.scope_summary?.slice(0, 80)}…</td>
                          <td className="px-4 py-3 text-slate-600">{(a.approver_names || []).join(', ') || '—'}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{a.signed_by}<br /><span className="text-slate-400">{(a.signed_at || '').slice(0, 10)}</span></td>
                          <td className="px-4 py-3 font-mono text-[10px] text-slate-500">{(a.signature_hash || '').slice(0, 16)}…</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trainings">
            <Card>
              <CardContent className="p-0">
                {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
                  trainings.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem formações registadas ainda.</div> :
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">Membro</th>
                        <th className="text-left px-4 py-3">Curso</th>
                        <th className="text-right px-4 py-3">Horas</th>
                        <th className="text-left px-4 py-3">Data</th>
                        <th className="text-left px-4 py-3">Tópicos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {trainings.map(t => (
                        <tr key={t.training_id} className="hover:bg-slate-50" data-testid={`training-${t.training_id}`}>
                          <td className="px-4 py-3 font-medium">{t.member_name}<br /><span className="text-xs text-slate-500">{t.member_email}</span></td>
                          <td className="px-4 py-3">{t.course_title}{t.provider && <span className="text-xs text-slate-500 block">{t.provider}</span>}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{t.hours}h</td>
                          <td className="px-4 py-3 text-slate-600">{t.completed_at}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(t.topics || []).slice(0, 3).map((tp, i) => (
                                <Badge key={i} variant="secondary" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">{tp}</Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members">
            <Card>
              <CardContent className="p-0">
                {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
                  members.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">
                      Nenhum utilizador com papel <code>board_member</code> registado.<br />
                      Vá a <a href="/settings/users" className="text-indigo-600 hover:underline inline-flex items-center gap-1">Utilizadores <ExternalLink className="h-3 w-3" /></a> e atribua o papel.
                    </div>
                  ) :
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">Nome</th>
                        <th className="text-left px-4 py-3">Email</th>
                        <th className="text-left px-4 py-3">Departamento</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {members.map(m => (
                        <tr key={m.user_id} data-testid={`member-${m.user_id}`}>
                          <td className="px-4 py-3 font-medium">{m.name}</td>
                          <td className="px-4 py-3 text-slate-600">{m.email}</td>
                          <td className="px-4 py-3 text-slate-500">{m.department || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Approval dialog */}
      <Dialog open={showApproval} onOpenChange={setShowApproval}>
        <DialogContent className="max-w-2xl" data-testid="board-approval-dialog">
          <DialogHeader>
            <DialogTitle>Nova aprovação anual (Art. 20(1) NIS2)</DialogTitle>
            <DialogDescription>Regista formalmente a aprovação anual das medidas de gestão de risco pelo Órgão de Gestão. Cria assinatura SHA-256 imutável.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ano *</Label>
                <Input type="number" value={approvalForm.year} onChange={e => setApprovalForm({ ...approvalForm, year: e.target.value })} data-testid="approval-form-year" />
              </div>
            </div>
            <div>
              <Label>Resumo do âmbito aprovado * (mín. 20 caracteres)</Label>
              <Textarea rows={4} value={approvalForm.scope_summary} onChange={e => setApprovalForm({ ...approvalForm, scope_summary: e.target.value })} placeholder="Ex: Aprovação da Política de Segurança da Informação v2.3, Plano de Continuidade de Negócio 2026, matriz de riscos residuais…" data-testid="approval-form-scope" />
            </div>
            <div>
              <Label>Aprovadores (separados por vírgula)</Label>
              <Input value={approvalForm.approver_names} onChange={e => setApprovalForm({ ...approvalForm, approver_names: e.target.value })} placeholder="Maria Silva, João Costa, Ana Santos" data-testid="approval-form-approvers" />
            </div>
            <div>
              <Label>Documentos aprovados (separados por vírgula)</Label>
              <Input value={approvalForm.approved_documents} onChange={e => setApprovalForm({ ...approvalForm, approved_documents: e.target.value })} placeholder="POL-SEG-INFO-v2.3, PLN-CONT-NEG-2026" data-testid="approval-form-documents" />
            </div>
            <div>
              <Label>Notas adicionais</Label>
              <Textarea rows={2} value={approvalForm.notes} onChange={e => setApprovalForm({ ...approvalForm, notes: e.target.value })} data-testid="approval-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowApproval(false)}>Cancelar</Button>
            <Button onClick={submitApproval} disabled={savingA} data-testid="approval-form-submit">
              {savingA ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A registar…</> : <><ShieldCheck className="h-4 w-4 mr-2" />Assinar e registar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training dialog */}
      <Dialog open={showTraining} onOpenChange={setShowTraining}>
        <DialogContent className="max-w-2xl" data-testid="board-training-dialog">
          <DialogHeader>
            <DialogTitle>Registar formação do Órgão de Gestão (Art. 20(2) NIS2)</DialogTitle>
            <DialogDescription>Prova documental de que o membro completou formação obrigatória em cibersegurança/IA.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome do membro *</Label>
                <Input value={trainingForm.member_name} onChange={e => setTrainingForm({ ...trainingForm, member_name: e.target.value })} data-testid="training-form-member-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={trainingForm.member_email} onChange={e => setTrainingForm({ ...trainingForm, member_email: e.target.value })} data-testid="training-form-member-email" />
              </div>
            </div>
            <div>
              <Label>Curso *</Label>
              <Input value={trainingForm.course_title} onChange={e => setTrainingForm({ ...trainingForm, course_title: e.target.value })} placeholder="Ex: NIS2 for Boards — Executive Program" data-testid="training-form-course" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Duração (horas) *</Label>
                <Input type="number" step="0.5" value={trainingForm.hours} onChange={e => setTrainingForm({ ...trainingForm, hours: e.target.value })} data-testid="training-form-hours" />
              </div>
              <div>
                <Label>Data conclusão *</Label>
                <Input type="date" value={trainingForm.completed_at} onChange={e => setTrainingForm({ ...trainingForm, completed_at: e.target.value })} data-testid="training-form-date" />
              </div>
              <div>
                <Label>Fornecedor</Label>
                <Input value={trainingForm.provider} onChange={e => setTrainingForm({ ...trainingForm, provider: e.target.value })} placeholder="INA, ISACA, Deloitte…" data-testid="training-form-provider" />
              </div>
            </div>
            <div>
              <Label>Tópicos (separados por vírgula)</Label>
              <Input value={trainingForm.topics} onChange={e => setTrainingForm({ ...trainingForm, topics: e.target.value })} placeholder="NIS2 Art. 20, AI Act Art. 4, gestão de crise, phishing" data-testid="training-form-topics" />
            </div>
            <div>
              <Label>Referência do certificado</Label>
              <Input value={trainingForm.certificate_ref} onChange={e => setTrainingForm({ ...trainingForm, certificate_ref: e.target.value })} placeholder="URL ou nº de certificado" data-testid="training-form-cert" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTraining(false)}>Cancelar</Button>
            <Button onClick={submitTraining} disabled={savingT} data-testid="training-form-submit">
              {savingT ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A registar…</> : 'Registar formação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
